import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, useFetch, errMsg } from "../lib/api";
import { btn, Loading, ErrorBox } from "./kit";

const time = (value) => value ? new Date(value).toLocaleString() : "Never";

const KIND_LABEL = { job: "Private / remote job", project: "Freelance project", gov_job: "Government job", scheme: "Scheme or benefit" };
const EMPTY_FORM = { name: "", kind: "gov_job", official_url: "", organisation: "", description: "", state: "", enabled: true };
const sourceMetrics = (source) => [
  { label: "Last successful sync", value: time(source.last_success_at) },
  { label: "Stored imports", value: source.imported_count },
  { label: "Awaiting review", value: source.review_count },
  { label: "Rejected by source gate", value: source.rejected_count ?? 0 },
  { label: "Duplicates in last run", value: source.last_run?.duplicates ?? 0 },
  { label: "Failures in last run", value: source.last_run?.failed ?? 0 },
  { label: "Expired records", value: source.expired_count },
  ...(source.health_monitored ? [
    {
      label: "Board health",
      value: source.health_status || "Not checked yet",
      testid: `automation-health-status-${source.id}`,
    },
    {
      label: "Last board check",
      value: time(source.health_checked_at),
      testid: `automation-health-checked-${source.id}`,
    },
    {
      label: "Eligible roles at check",
      value: source.health_eligible_records ?? "—",
      testid: `automation-health-records-${source.id}`,
    },
  ] : []),
];

export const AutomationStatus = () => {
  const r = useFetch("/admin/automation");
  const [busy, setBusy] = useState(false);
  const [healthBusy, setHealthBusy] = useState(false);
  const [sourceBusy, setSourceBusy] = useState("");
  const [togglingSource, setTogglingSource] = useState("");
  const [form, setForm] = useState(EMPTY_FORM);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const sync = async () => {
    setBusy(true);
    try {
      await api.post("/admin/automation/sync");
      toast.success("Import queued. Refresh status shortly to see the result.");
      r.reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const syncOne = async (id) => {
    setSourceBusy(id);
    try {
      await api.post(`/admin/automation/sync/${id}`);
      toast.success("Source sync queued. Refresh status in a few seconds.");
      r.reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setSourceBusy(""); }
  };

  const checkEmployerHealth = async () => {
    setHealthBusy(true);
    try {
      await api.post("/admin/automation/health");
      toast.success("Employer board check queued. Refresh status shortly to see the result.");
      r.reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setHealthBusy(false); }
  };

  const toggle = async (id, enabled) => {
    setTogglingSource(id);
    try {
      await api.put(`/admin/automation/sources/${id}`, { enabled });
      toast.success(enabled ? "Source enabled." : "Source disabled.");
      r.reload();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setTogglingSource(""); }
  };

  const startEdit = (m) => {
    setEditing(m.id);
    setForm({ name: m.name, kind: m.kind, official_url: m.official_url, organisation: m.organisation || "", description: m.description || "", state: m.state || "", enabled: m.enabled });
  };
  const resetForm = () => { setEditing(null); setForm(EMPTY_FORM); };

  const saveManual = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/admin/sources/manual/${editing}`, form);
        toast.success("Manual source updated.");
      } else {
        await api.post("/admin/sources/manual", form);
        toast.success("Manual source added.");
      }
      resetForm();
      r.reload();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setSaving(false); }
  };

  const removeManual = async (id) => {
    if (!window.confirm("Remove this manual source link from the directory?")) return;
    try {
      await api.delete(`/admin/sources/manual/${id}`);
      toast.success("Removed.");
      r.reload();
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (r.loading) return <Loading />;
  if (r.error) return <ErrorBox message={r.error} onRetry={r.reload} />;
  const running = r.data.runs.some((run) => ["queued", "running"].includes(run.status) && Date.now() - new Date(run.created_at).getTime() < 10 * 60 * 1000);
  const manual = r.data.manual_sources || [];

  return <section className="space-y-4" data-testid="automation-status">
    <h2 className="text-lg font-bold">Opportunity collection</h2>
    <p className="text-sm text-sub" data-testid="automation-coverage">{r.data.coverage}</p>
    <p className="text-sm text-sub" data-testid="automation-schedule">Configured schedule: {r.data.schedule}. Run history below confirms completed work; configuration alone does not prove a scheduled run.</p>
    <p className="text-sm text-sub" data-testid="employer-health-schedule">
      Employer board health: {r.data.health_schedule}
    </p>
    <div className="flex flex-wrap gap-2">
      <button className={btn()} onClick={sync} disabled={busy || running} data-testid="automation-sync">{running ? "Import running…" : busy ? "Queuing…" : "Sync all now"}</button>
      <button className={btn("secondary")} onClick={checkEmployerHealth} disabled={healthBusy} data-testid="employer-health-check">
        {healthBusy ? "Queuing…" : "Check employer boards"}
      </button>
      <button className={btn("secondary")} onClick={r.reload} data-testid="automation-refresh">Refresh status</button>
      <Link className={btn("ghost")} to="/admin?tab=jobs" data-testid="automation-review-jobs">Review imported jobs</Link>
    </div>

    {r.data.sources.map((s) => <article className="card space-y-3 p-5" key={s.id} data-testid={`automation-source-${s.id}`}>
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="font-bold">{s.name}</h3>
          <p className="text-sm text-sub">{s.enabled ? (s.trusted ? "Enabled · Validated records auto-publish" : "Enabled · Manual review required") : "Disabled by administrator or by default"} · Runs every {s.cadence_hours}h</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button className={btn("secondary")} onClick={() => syncOne(s.id)} disabled={sourceBusy === s.id || s.adapter === "manual"} data-testid={`automation-sync-${s.id}`}>{sourceBusy === s.id ? "Queuing…" : "Sync now"}</button>
          <button className={btn(s.enabled ? "ghost" : "primary")} onClick={() => toggle(s.id, !s.enabled)} disabled={togglingSource === s.id} data-testid={`automation-toggle-${s.id}`}>{togglingSource === s.id ? "Saving…" : s.enabled ? "Disable" : "Enable"}</button>
        </div>
      </header>
      {s.status_note && <p className="text-sm text-sub" data-testid={`automation-source-note-${s.id}`}>{s.status_note}</p>}
      <a href={s.terms_url} target="_blank" rel="noopener noreferrer" className="link text-sm" data-testid={`automation-terms-${s.id}`}>Source usage terms (opens in new tab)</a>
      <dl className="grid gap-3 sm:grid-cols-3">
        {sourceMetrics(s).map(({ label: metricLabel, value, testid }, i) => (
          <div key={metricLabel}>
            <dt className="text-sm text-sub">{metricLabel}</dt>
            <dd
              className="font-semibold"
              data-testid={testid || `automation-${s.id}-metric-${i}`}
            >
              {value}
            </dd>
          </div>
        ))}
      </dl>
      {s.last_error && <p className="text-sm text-err" role="alert" data-testid={`automation-error-${s.id}`}>{s.last_error}</p>}
      {s.health_error && <p className="text-sm text-err" role="alert" data-testid={`automation-health-error-${s.id}`}>{s.health_error}</p>}
      {s.last_run?.errors?.length > 0 && <ul className="list-disc pl-5 text-sm text-err" data-testid={`automation-failures-${s.id}`}>{s.last_run.errors.map((e, i) => <li key={i}>{e}</li>)}</ul>}
    </article>)}

    <section className="card space-y-4 p-5" data-testid="manual-sources">
      <header>
        <h3 className="font-bold">Admin-managed source directory</h3>
        <p className="text-sm text-sub">Add official sources without a permitted automated feed (RRB, IBPS, NCS, NSP, MyScheme, state portals, MNC career pages). These appear to users as a reviewed "Other official sources" list on the matching section.</p>
      </header>
      <form onSubmit={saveManual} className="grid gap-3 sm:grid-cols-2" data-testid="manual-source-form">
        <label className="space-y-1.5"><span className="block font-semibold">Source name</span>
          <input className="field" required maxLength={200} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="e.g. Railway Recruitment Boards (RRB)" data-testid="manual-source-name" />
        </label>
        <label className="space-y-1.5"><span className="block font-semibold">Opportunity type</span>
          <select className="field" value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} data-testid="manual-source-kind">
            {Object.entries(KIND_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
          </select>
        </label>
        <label className="space-y-1.5 sm:col-span-2"><span className="block font-semibold">Official HTTPS URL</span>
          <input className="field" required type="url" pattern="https://.*" maxLength={500} value={form.official_url} onChange={(e) => setForm({ ...form, official_url: e.target.value })} placeholder="https://indianrailways.gov.in/..." data-testid="manual-source-url" />
        </label>
        <label className="space-y-1.5"><span className="block font-semibold">Organisation <span className="text-sm font-normal text-sub">(optional)</span></span>
          <input className="field" maxLength={200} value={form.organisation} onChange={(e) => setForm({ ...form, organisation: e.target.value })} data-testid="manual-source-org" />
        </label>
        <label className="space-y-1.5"><span className="block font-semibold">State <span className="text-sm font-normal text-sub">(optional)</span></span>
          <input className="field" maxLength={120} value={form.state} onChange={(e) => setForm({ ...form, state: e.target.value })} placeholder="Central / All India / Maharashtra …" data-testid="manual-source-state" />
        </label>
        <label className="space-y-1.5 sm:col-span-2"><span className="block font-semibold">Short description <span className="text-sm font-normal text-sub">(what users will find there)</span></span>
          <textarea className="field" rows={3} maxLength={1200} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="manual-source-desc" />
        </label>
        <label className="flex items-center gap-2 sm:col-span-2">
          <input type="checkbox" checked={form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} data-testid="manual-source-enabled" />
          <span>Show this link to users</span>
        </label>
        <div className="sm:col-span-2 flex flex-wrap gap-2">
          <button className={btn()} disabled={saving} data-testid="manual-source-save">{saving ? "Saving…" : editing ? "Update source" : "Add source"}</button>
          {editing && <button type="button" className={btn("ghost")} onClick={resetForm} data-testid="manual-source-cancel">Cancel edit</button>}
        </div>
      </form>
      {manual.length === 0 ? (
        <p className="text-sm text-sub" data-testid="manual-source-empty">No admin-managed sources yet. Add one above.</p>
      ) : (
        <ul className="space-y-2" data-testid="manual-source-list">
          {manual.map((m) => (
            <li key={m.id} className="border-t border-line pt-3 text-sm" data-testid={`manual-source-${m.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-semibold">{m.name} <span className="font-normal text-sub">· {KIND_LABEL[m.kind]} · {m.enabled ? "Visible" : "Hidden"}</span></p>
                  {m.organisation && <p className="text-sub">{m.organisation}{m.state ? ` · ${m.state}` : ""}</p>}
                  {m.description && <p>{m.description}</p>}
                  <a
                    className="link break-all"
                    href={m.official_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`manual-source-link-${m.id}`}
                  >
                    {m.official_url}
                  </a>
                </div>
                <div className="flex gap-2">
                  <button className={btn("ghost")} onClick={() => startEdit(m)} data-testid={`manual-source-edit-${m.id}`}>Edit</button>
                  <button className={btn("ghost")} onClick={() => removeManual(m.id)} data-testid={`manual-source-remove-${m.id}`}>Remove</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>

    <p className="text-sm text-sub" data-testid="automation-limitations">{r.data.limitations}</p>
    <div className="card p-5"><h3 className="font-bold">Recent runs</h3>
      <ul className="mt-3 space-y-2" data-testid="automation-runs">{r.data.runs.length ? r.data.runs.map((run) => <li key={run.id} className="border-t border-line pt-2 text-sm" data-testid={`automation-run-${run.id}`}>
        {run.trigger === "scheduled_endpoint" ? "Scheduled endpoint" : run.trigger === "manual_source" ? "Manual single source" : "Manual"} · {run.status} · {time(run.finished_at || run.created_at)}
        {run.error && <span className="block text-err">{run.error}</span>}
      </li>) : <li>No imports have run yet.</li>}</ul>
    </div>
  </section>;
};
